export interface PendingGoal {
  name?: string;
  goalTypeId?: number;
  weightage?: number;
  status?: string;
  description?: string;
}

export interface PendingDeleteGoal {
  awaitingName?: boolean;
}

export const defaultStatus = "active";

export const startCreateGoalFlow = (
  setPendingGoal: (data: PendingGoal | null) => void,
  pushAIChat: (msg: string) => void,
  speak: (msg: string) => void,
  nameFromIntent?: string
) => {
  if (nameFromIntent) {
    setPendingGoal({ name: nameFromIntent });

    pushAIChat(
      `Creating goal "${nameFromIntent}". Which goal type should I use?`
    );
    speak("Which goal type should I use?");
    return;
  }

  setPendingGoal({});
  pushAIChat("Sure! What should the goal name be?");
  speak("What should the goal name be?");
};

export const processPendingGoal = (
  text: string,
  pendingGoal: PendingGoal,
  setPendingGoal: (data: PendingGoal | null) => void,
  pushAIChat: (msg: string) => void,
  speak: (msg: string) => void,
  helpers: {
    getGoalTypeIdByName: (name: string) => number | null;
  }
) => {

  // STEP 1 — Name
  if (!pendingGoal.name) {
    const cleanName = text.trim();
    setPendingGoal({ ...pendingGoal, name: cleanName });

    pushAIChat("Which goal type?");
    speak("Which goal type?");
    return;
  }

  // STEP 2 — Goal Type
  if (!pendingGoal.goalTypeId) {
    const typeId = helpers.getGoalTypeIdByName(text);

    if (!typeId) {
      pushAIChat("I couldn’t find that goal type. Try again.");
      speak("Please say a valid goal type.");
      return;
    }

    setPendingGoal({ ...pendingGoal, goalTypeId: typeId });

    pushAIChat("What should be the weightage?");
    speak("What should be the weightage?");
    return;
  }

  // STEP 3 — Weightage
  if (!pendingGoal.weightage) {
    const weight = Number(text);
    if (isNaN(weight)) {
      pushAIChat("Weightage must be a number.");
      speak("Please say a valid number.");
      return;
    }

    setPendingGoal({ ...pendingGoal, weightage: weight });

    pushAIChat("Should it be Active or Inactive?");
    speak("Should it be active or inactive?");
    return;
  }

  // STEP 4 — Status & CREATE
  if (!pendingGoal.status) {
    const status = text.includes("inactive") ? "inactive" : "active";

    pushAIChat(`Creating goal "${pendingGoal.name}"...`);
    speak(`Creating goal ${pendingGoal.name}`);

    window.dispatchEvent(
      new CustomEvent("ai-create-goal", {
        detail: {
          Name: pendingGoal.name,
          Goaltype: pendingGoal.goalTypeId,
          Weightage: pendingGoal.weightage,
          Status: status,
          Description: pendingGoal.description || ""
        }
      })
    );

    setPendingGoal(null);
  }
};

export const startDeleteGoalFlow = (
  setPendingDelete: (data: PendingDeleteGoal | null) => void,
  pushAIChat: (msg: string) => void,
  speak: (msg: string) => void,
  nameFromIntent?: string
) => {
  if (nameFromIntent) {
    pushAIChat(`Deleting goal "${nameFromIntent}"...`);
    speak(`Deleting goal ${nameFromIntent}`);

    window.dispatchEvent(
      new CustomEvent("ai-delete-goal", {
        detail: { name: nameFromIntent }
      })
    );
    return;
  }

  setPendingDelete({ awaitingName: true });
  pushAIChat("Which goal do you want to delete?");
  speak("Which goal do you want to delete?");
};

export const processPendingDeleteGoal = (
  text: string,
  pendingDelete: PendingDeleteGoal,
  setPendingDelete: (data: PendingDeleteGoal | null) => void,
  pushAIChat: (msg: string) => void,
  speak: (msg: string) => void
) => {
  if (!pendingDelete.awaitingName) return;

  const cleanName = text.trim();

  pushAIChat(`Deleting goal "${cleanName}"...`);
  speak(`Deleting goal ${cleanName}`);

  window.dispatchEvent(
    new CustomEvent("ai-delete-goal", {
      detail: { name: cleanName }
    })
  );

  setPendingDelete(null);
};

